import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { supabase } from '../lib/supabase'
import { BadgeCheck, CalendarDays, Users, MapPin, XCircle, Edit, Save, X, UserCircle2 } from 'lucide-react'

type MovieRow = {
  id: number
  title: string
  organizer_id: number | null
  accept_booking?: boolean
  booking_start_time?: string | null
  booking_stop_time?: string | null
  booking_stop_enabled?: boolean
  description: string
  image_url: string
  duration: number
  is_canceled?: boolean
}

type ScreeningRow = { id: number; movie_id: number; start_time: string; location: string; duration?: number | null }
type SeatSplitRow = { screening_id: number; silver_total: number; gold_total: number; diamond_total: number; silver_booked: number; gold_booked: number; diamond_booked: number; silver_price?: number; gold_price?: number; diamond_price?: number }
type BookingRow = { id: number; screening_id: number; status: string; total_amount?: number; payment_status?: string; seats?: number }
type SeatLayoutRow = { screening_id: number; rows: number; cols: number; seats: boolean[][] }
type BookingSeatsRow = { screening_id: number; seat_ids?: string[]; seat_id?: string }
type OrganizerRow = { id: number; name: string }

interface Props { movieId: number; onClose: () => void }

export default function AdminEventDetailsModal({ movieId, onClose }: Props) {
  const [movie, setMovie] = useState<MovieRow | null>(null)
  const [screenings, setScreenings] = useState<ScreeningRow[]>([])
  const [splits, setSplits] = useState<SeatSplitRow[]>([])
  const [bookings, setBookings] = useState<BookingRow[]>([])
  const [layoutsByScreening, setLayoutsByScreening] = useState<Record<number, SeatLayoutRow>>({})
  const [bookingSeatsRows, setBookingSeatsRows] = useState<BookingSeatsRow[]>([])
  const [organizer, setOrganizer] = useState<OrganizerRow | null>(null)
  const [organizers, setOrganizers] = useState<OrganizerRow[]>([])
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<{ title: string; description: string; image_url: string; is_canceled: boolean; accept_booking: boolean; booking_start_time: string; booking_stop_time: string; booking_stop_enabled: boolean; organizer_id: number | '' } | null>(null)
  const [latestEdit, setLatestEdit] = useState<{ start_time: string; location: string; duration: number | '' }>({ start_time: '', location: '', duration: '' })
  const [saving, setSaving] = useState(false)
  const [silverPct, setSilverPct] = useState(0)
  const [goldPct, setGoldPct] = useState(0)
  const [diamondPct, setDiamondPct] = useState(0)

  useEffect(() => {
    ; (async () => {
      const { data: m } = await supabase.from('movies').select('*').eq('id', movieId).limit(1)
      const mv = m && m.length ? (m[0] as MovieRow) : null
      setMovie(mv)
      if (mv?.organizer_id) {
        const { data: org } = await supabase.from('organizers').select('id,name').eq('id', mv.organizer_id).limit(1)
        setOrganizer(org && org.length ? (org[0] as OrganizerRow) : null)
      } else setOrganizer(null)
      const { data: ss } = await supabase.from('screenings').select('id,movie_id,start_time,location,duration').eq('movie_id', movieId).order('start_time', { ascending: true })
      const sRows = ((ss || []) as ScreeningRow[])
      setScreenings(sRows)
      const ids = sRows.map(s => s.id)
      let spData: SeatSplitRow[] = []
      if (ids.length) {
        const [{ data: sp }, { data: b }, { data: bsAll }, { data: layouts }] = await Promise.all([
          supabase.from('seatssplitting').select('*').in('screening_id', ids),
          supabase.from('bookings').select('id,screening_id,status,total_amount,payment_status,seats').in('screening_id', ids),
          supabase.from('booking_seats').select('screening_id, seat_ids, seat_id').in('screening_id', ids),
          supabase.from('seat_layouts').select('*').in('screening_id', ids)
        ])
        spData = (sp || []) as SeatSplitRow[]
        setSplits(spData)
        setBookings(((b || []) as BookingRow[]))
        setBookingSeatsRows(((bsAll || []) as BookingSeatsRow[]))
        const map: Record<number, SeatLayoutRow> = {}
        for (const l of ((layouts || []) as any[])) map[l.screening_id] = { screening_id: l.screening_id as number, rows: l.rows as number, cols: l.cols as number, seats: (l.seats as any) as boolean[][] }
        setLayoutsByScreening(map)
      }
      const { data: orgs } = await supabase.from('organizers').select('id,name').order('name')
      setOrganizers(((orgs || []) as OrganizerRow[]))
      // Calculate split percentages
      if (sRows.length && spData.length) {
        const latestScr = sRows.reduce((a, b) => new Date(a.start_time) > new Date(b.start_time) ? a : b)
        const row = spData.find(s => s.screening_id === latestScr.id)
        if (row) {
          const sum = row.silver_total + row.gold_total + row.diamond_total
          if (sum > 0) {
            setSilverPct(Math.round((row.silver_total / sum) * 100))
            setGoldPct(Math.round((row.gold_total / sum) * 100))
            setDiamondPct(Math.round((row.diamond_total / sum) * 100))
          }
        }
      }
      if (mv) setForm({
        title: mv.title,
        description: mv.description || '',
        image_url: mv.image_url || '',
        is_canceled: !!mv.is_canceled,
        accept_booking: !!mv.accept_booking,
        booking_start_time: mv.booking_start_time || '',
        booking_stop_time: mv.booking_stop_time || '',
        booking_stop_enabled: !!mv.booking_stop_enabled,
        organizer_id: mv.organizer_id || ''
      })
      const latest = sRows.length ? sRows.reduce((a, b) => new Date(a.start_time) > new Date(b.start_time) ? a : b) : null
      if (latest) setLatestEdit({ start_time: latest.start_time, location: latest.location, duration: (latest.duration as any) || '' })
    })()
  }, [movieId])

  const latest = useMemo(() => screenings.length ? screenings.reduce((a, b) => new Date(a.start_time) > new Date(b.start_time) ? a : b) : null, [screenings])
  const first = useMemo(() => screenings.length ? screenings.reduce((a, b) => new Date(a.start_time) < new Date(b.start_time) ? a : b) : null, [screenings])
  const status = useMemo(() => {
    const now = Date.now()
    const upcoming = screenings.find(s => new Date(s.start_time).getTime() > now)
    if (movie?.is_canceled) return 'cancelled'
    return upcoming ? 'active' : 'completed'
  }, [movie, screenings])

  const bookingsTotal = useMemo(() => {
    const ids = screenings.map(s => s.id)
    const splitsById: Record<number, SeatSplitRow> = {}
    for (const row of splits) splitsById[row.screening_id] = row
    let total = 0
    const confirmedBySid: Record<number, number> = {}
    for (const b of bookings) {
      if ((b.status || '') === 'confirmed') confirmedBySid[b.screening_id] = (confirmedBySid[b.screening_id] || 0) + (Number(b.seats || 0))
    }
    total = ids.reduce((acc, sid) => {
      const bsForSid: BookingSeatsRow[] = bookingSeatsRows.filter(x => Number(x.screening_id) === sid)
      const bookedLayout = bsForSid.reduce((sum, r) => sum + (Array.isArray(r.seat_ids) ? (r.seat_ids as any[]).length : (r.seat_id ? 1 : 0)), 0)
      const split = splitsById[sid]
      const bookedSplit = split ? (split.silver_booked + split.gold_booked + split.diamond_booked) : 0
      const bookedFromBookings = confirmedBySid[sid] || 0
      const chosen = bookedLayout > 0 ? bookedLayout : (split ? bookedSplit : bookedFromBookings)
      return acc + chosen
    }, 0)
    return total
  }, [screenings, splits, bookings, bookingSeatsRows])

  const availableTotal = useMemo(() => {
    if (!latest) return 0
    const layout = layoutsByScreening[latest.id]
    if (layout) {
      let capacity = 0
      for (let r = 0; r < layout.rows; r++) for (let c = 0; c < layout.cols; c++) if (layout.seats[r][c]) capacity++
      return Math.max(0, capacity - bookingsTotal)
    }
    const split = splits.find(x => x.screening_id === latest.id)
    if (split) {
      const total = (split.silver_total || 0) + (split.gold_total || 0) + (split.diamond_total || 0)
      const booked = (split.silver_booked || 0) + (split.gold_booked || 0) + (split.diamond_booked || 0)
      return Math.max(0, total - booked)
    }
    return 0
  }, [latest, layoutsByScreening, splits, bookingsTotal])

  const save = async () => {
    if (!form || !movie) return
    setSaving(true)
    try {
      const updates: Partial<MovieRow> = {
        title: form.title,
        description: form.description,
        image_url: form.image_url,
        is_canceled: form.is_canceled,
        accept_booking: form.accept_booking,
        booking_start_time: form.booking_start_time || null,
        booking_stop_time: form.booking_stop_time || null,
        booking_stop_enabled: form.booking_stop_enabled,
        organizer_id: form.organizer_id === '' ? null : Number(form.organizer_id)
      }
      const { error: movieError } = await supabase.from('movies').update(updates).eq('id', movie.id)
      if (movieError) throw new Error(movieError.message)

      // Save Seat Splits / Pricing
      if (latest && (silverPct + goldPct + diamondPct === 100)) {
        const row = splits.find(s => s.screening_id === latest.id)
        const currentTotal = row ? (row.silver_total + row.gold_total + row.diamond_total) : 100 // default capacity base if missing?
        // Actually we should rely on existing total capacity logic but for simplicity we re-distribute current total
        const silver = Math.round((currentTotal * silverPct) / 100)
        const gold = Math.round((currentTotal * goldPct) / 100)
        const diamond = currentTotal - silver - gold

        const patch = {
          silver_total: silver,
          gold_total: gold,
          diamond_total: diamond,
          silver_price: row?.silver_price,
          gold_price: row?.gold_price,
          diamond_price: row?.diamond_price
        }
        await supabase.from('seatssplitting').update(patch).eq('screening_id', latest.id)

        // update local state for pricing inputs to reflect?
        // Actually pricing inputs update local state directly so we just need to push 'splits' changes if any
        // But here we might be saving from the 'splits' state which should have been updated by inputs
        if (row) {
          await supabase.from('seatssplitting').update({
            silver_price: row.silver_price,
            gold_price: row.gold_price,
            diamond_price: row.diamond_price
          }).eq('screening_id', latest.id)
        }
      }
      const { error: finalError } = await supabase.from('movies').update(updates).eq('id', movie.id)
      if (finalError) throw new Error(finalError.message)
      if (latest) {
        const latestUpdates: Partial<ScreeningRow> = {
          start_time: latestEdit.start_time,
          location: latestEdit.location,
          duration: latestEdit.duration === '' ? null : Number(latestEdit.duration)
        }
        const { error: se } = await supabase.from('screenings').update(latestUpdates).eq('id', latest.id)
        if (se) throw new Error(se.message)
      }
      setEditing(false)
    } catch {
    } finally {
      setSaving(false)
    }
  }

  if (!movie) return null

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            {status === 'active' ? <BadgeCheck className="text-green-500 h-5 w-5" /> : status === 'cancelled' ? <XCircle className="text-red-500 h-5 w-5" /> : <BadgeCheck className="text-gray-400 h-5 w-5" />}
            {editing ? (
              <input value={form?.title || ''} onChange={e => setForm(f => ({ ...(f as any), title: e.target.value }))} className="text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-3 py-2 w-64" />
            ) : (
              <div className="flex flex-col">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{movie.title}</h2>
                <div className="flex gap-2 mt-1">
                  <button onClick={async () => {
                    if (!movie) return
                    const ok = window.confirm('Run capture for all pending bookings?')
                    if (!ok) return
                    try {
                      const { data: bks } = await supabase.from('bookings').select('id, payment_status, screening_id').in('screening_id', screenings.map(s => s.id))
                      const targetIds = ((bks || []) as any[]).filter(b => b.payment_status === 'requires_capture').map(b => b.id)
                      let count = 0
                      for (const bid of targetIds) {
                        await supabase.functions.invoke('capture-payment-intent', { body: { booking_id: bid } })
                        count++
                      }
                      alert(`Captured ${count} bookings`)
                    } catch (e) { alert('Error running captures') }
                  }} className="text-xs bg-indigo-600 px-2 py-1 rounded text-white">Run Captures</button>
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {!editing && <button onClick={() => setEditing(true)} className="px-3 py-2 bg-indigo-600 text-white rounded flex items-center gap-2"><Edit className="h-4 w-4" />Edit</button>}
            {editing && <>
              <button onClick={save} disabled={saving} className="px-3 py-2 bg-green-600 text-white rounded flex items-center gap-2 disabled:opacity-50"><Save className="h-4 w-4" />Save</button>
              <button onClick={() => setEditing(false)} className="px-3 py-2 bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-white rounded flex items-center gap-2"><X className="h-4 w-4" />Cancel</button>
            </>}
            <button onClick={onClose} className="px-3 py-2 bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-white rounded">Close</button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="bg-gray-100 dark:bg-gray-700 rounded p-4">
              <p className="text-white font-medium mb-2">Schedule</p>
              <div className="flex items-center gap-2 text-gray-300"><CalendarDays className="h-4 w-4" /><span>{first ? new Date(first.start_time).toLocaleString() : '—'} → {latest ? new Date(latest.start_time).toLocaleString() : '—'}</span></div>
              <div className="flex items-center gap-2 text-gray-300 mt-2"><MapPin className="h-4 w-4" /><span>{latest?.location || '—'}</span></div>
              {editing && latest && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <input type="datetime-local" value={latestEdit.start_time} onChange={e => setLatestEdit(v => ({ ...v, start_time: e.target.value }))} className="rounded-lg text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-700 h-10 px-3" />
                  <input type="text" value={latestEdit.location} onChange={e => setLatestEdit(v => ({ ...v, location: e.target.value }))} placeholder="Location" className="rounded-lg text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-700 h-10 px-3" />
                  <input type="number" value={latestEdit.duration === '' ? '' : latestEdit.duration} onChange={e => setLatestEdit(v => ({ ...v, duration: e.target.value ? Number(e.target.value) : '' }))} placeholder="Duration (min)" className="rounded-lg text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-700 h-10 px-3" />
                </div>
              )}
            </div>
            <div className="bg-gray-700 rounded p-4">
              <p className="text-white font-medium mb-2">Booking Window</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Open</label>
                  {editing ? (
                    <input type="datetime-local" value={form?.booking_start_time || ''} onChange={e => setForm(f => ({ ...(f as any), booking_start_time: e.target.value }))} className="w-full rounded-lg text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-700 h-10 px-3" />
                  ) : (
                    <p className="text-gray-300">{movie.booking_start_time ? new Date(movie.booking_start_time).toLocaleString() : '—'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Close</label>
                  {editing ? (
                    <input type="datetime-local" value={form?.booking_stop_time || ''} onChange={e => setForm(f => ({ ...(f as any), booking_stop_time: e.target.value }))} className="w-full rounded-lg text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-700 h-10 px-3" />
                  ) : (
                    <p className="text-gray-300">{movie.booking_stop_time ? new Date(movie.booking_stop_time).toLocaleString() : '—'}</p>
                  )}
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-3">
                <label className="flex items-center gap-2 text-gray-300">
                  <input type="checkbox" disabled={!editing} checked={!!form?.booking_stop_enabled} onChange={e => setForm(f => ({ ...(f as any), booking_stop_enabled: e.target.checked }))} />
                  <span>Stop enabled</span>
                </label>
                <label className="flex items-center gap-2 text-gray-300">
                  <input type="checkbox" disabled={!editing} checked={!!form?.accept_booking} onChange={e => setForm(f => ({ ...(f as any), accept_booking: e.target.checked }))} />
                  <span>Accept booking</span>
                </label>
                <label className="flex items-center gap-2 text-gray-300">
                  <input type="checkbox" disabled={!editing} checked={!!form?.is_canceled} onChange={e => setForm(f => ({ ...(f as any), is_canceled: e.target.checked }))} />
                  <span>Cancelled</span>
                </label>
              </div>
            </div>
            {/* Description & Image Editing */}
            <div className="bg-gray-700 rounded p-4 space-y-3">
              <p className="text-white font-medium">Details</p>
              <div>
                <label className="text-sm text-gray-300">Description</label>
                {editing ? (
                  <textarea value={form?.description || ''} onChange={e => setForm(f => ({ ...(f as any), description: e.target.value }))} className="w-full rounded bg-gray-600 text-white border-none text-sm p-2" rows={3} />
                ) : (
                  <p className="text-gray-300 text-sm max-h-20 overflow-y-auto">{movie.description || '—'}</p>
                )}
              </div>
              <div>
                <label className="text-sm text-gray-300">Image URL</label>
                {editing ? (
                  <input value={form?.image_url || ''} onChange={e => setForm(f => ({ ...(f as any), image_url: e.target.value }))} className="w-full rounded bg-gray-600 text-white border-none text-sm p-2" />
                ) : (
                  <p className="text-gray-300 text-sm truncate">{movie.image_url || '—'}</p>
                )}
              </div>
            </div>
            {/* Seat Splitting / Pricing */}
            {splits.length > 0 && latest && (
              <div className="bg-gray-700 rounded p-4 space-y-3">
                <p className="text-white font-medium">Seat Configuration & Pricing</p>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs text-slate-300">Silver ({silverPct}%)</label>
                    {editing ? <input type="range" min="0" max="100" value={silverPct} onChange={e => setSilverPct(Number(e.target.value))} className="w-full h-1" /> : null}
                    <input
                      disabled={!editing}
                      type="number" placeholder="Price"
                      value={splits.find(s => s.screening_id === latest.id)?.silver_price ?? 0}
                      onChange={e => {
                        const val = Number(e.target.value)
                        setSplits(prev => prev.map(p => p.screening_id === latest.id ? { ...p, silver_price: val } : p))
                      }}
                      className="w-full text-xs p-1 rounded bg-gray-600 text-white mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-yellow-500">Gold ({goldPct}%)</label>
                    {editing ? <input type="range" min="0" max="100" value={goldPct} onChange={e => setGoldPct(Number(e.target.value))} className="w-full h-1" /> : null}
                    <input
                      disabled={!editing}
                      type="number" placeholder="Price"
                      value={splits.find(s => s.screening_id === latest.id)?.gold_price ?? 0}
                      onChange={e => {
                        const val = Number(e.target.value)
                        setSplits(prev => prev.map(p => p.screening_id === latest.id ? { ...p, gold_price: val } : p))
                      }}
                      className="w-full text-xs p-1 rounded bg-gray-600 text-white mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-blue-400">Diamond ({diamondPct}%)</label>
                    {editing ? <input type="range" min="0" max="100" value={diamondPct} onChange={e => setDiamondPct(Number(e.target.value))} className="w-full h-1" /> : null}
                    <input
                      disabled={!editing}
                      type="number" placeholder="Price"
                      value={splits.find(s => s.screening_id === latest.id)?.diamond_price ?? 0}
                      onChange={e => {
                        const val = Number(e.target.value)
                        setSplits(prev => prev.map(p => p.screening_id === latest.id ? { ...p, diamond_price: val } : p))
                      }}
                      className="w-full text-xs p-1 rounded bg-gray-600 text-white mt-1"
                    />
                  </div>
                </div>
                {editing && (silverPct + goldPct + diamondPct !== 100) && <p className="text-red-400 text-xs text-center">Total must be 100%</p>}
              </div>
            )}
          </div>
          <div className="space-y-4">
            <div className="bg-gray-700 rounded p-4">
              <p className="text-white font-medium mb-2">Organizer</p>
              <div className="flex items-center gap-2 text-gray-300">
                <UserCircle2 className="h-4 w-4" />
                {editing ? (
                  <select value={form?.organizer_id as any} onChange={e => setForm(f => ({ ...(f as any), organizer_id: e.target.value ? Number(e.target.value) : '' }))} className="rounded-lg text-white border border-gray-700 bg-gray-700 h-10 px-3">
                    <option value="">Unassigned</option>
                    {organizers.map(o => (<option key={o.id} value={o.id}>{o.name}</option>))}
                  </select>
                ) : (
                  <span>{organizer?.name || '—'}</span>
                )}
              </div>
            </div>
            <div className="bg-gray-700 rounded p-4">
              <p className="text-white font-medium mb-2">Counts</p>
              <div className="grid grid-cols-3 gap-3 text-gray-300">
                <div className="flex items-center gap-2"><Users className="h-4 w-4" /><span>Bookings: {bookingsTotal}</span></div>
                <div className="flex items-center gap-2"><Users className="h-4 w-4" /><span>Available: {availableTotal}</span></div>
                <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4" /><span>{latest ? new Date(latest.start_time).toLocaleDateString() : '—'}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
